import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  LinearProgress,
} from "@mui/material";
import { Close as CloseIcon, OpenInNew as OpenInNewIcon, Schedule as ScheduleIcon, PersonOutline as PersonIcon, DownloadOutlined } from "@mui/icons-material";
import type { JobDisplayData } from "@/types/jobs";
import { downloadJobResult } from "@/services/jobs-api";
import { JOB_TYPE_DESIGN, MONO_SX, SECTION_LABEL_SX, CHIP_GEOMETRIC_SX } from "./jobs-design-tokens";
import { JobResultRenderer } from "./JobResultRenderer";
import { JobDetailDrawer } from "./JobDetailDrawer";

export const STATUS_LABEL: Record<string, string> = {
  Queued: "En cola",
  Running: "En proceso",
  Completed: "Completado",
  Failed: "Fallido",
};

export const STATUS_COLOR: Record<string, "warning" | "info" | "success" | "error"> = {
  Queued: "warning",
  Running: "info",
  Completed: "success",
  Failed: "error",
};

export const JOB_TYPE_LABEL: Record<string, string> = {
  BookingReconciliation: "Conciliación de bookings",
  ContractAssignment: "Asignación de contratos",
  EligibleInvoicesReport: "Reporte facturas elegibles",
  CurrencyRateSync: "Sincronización de tasas de cambio",
};

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

interface Props {
  job: JobDisplayData;
  onClose: () => void;
}

export function JobDetailDialog({ job, onClose }: Props) {
  const [view, setView] = useState<"dialog" | "drawer">("dialog");
  const [downloadedResult, setDownloadedResult] = useState<unknown>(undefined);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);

  const isCompleted = job.status === "Completed";
  const typeDesign = JOB_TYPE_DESIGN[job.jobType];

  useEffect(() => {
    if (!isCompleted) return;
    let cancelled = false;
    setDownloading(true);
    setDownloadError(false);

    downloadJobResult(job.id, job.service)
      .then((blob) => blob.text())
      .then((text) => { if (!cancelled) setDownloadedResult(JSON.parse(text)); })
      .catch(() => {
        // Some job types (e.g., CurrencyRateSync) don't have blob storage references,
        // so download fails. That's OK — we'll display the inline resultPayload instead.
        if (!cancelled) setDownloadError(false);
      })
      .finally(() => { if (!cancelled) setDownloading(false); });

    return () => { cancelled = true; };
  }, [job.id, job.service, isCompleted]);

  function handleRetry() {
    setDownloadError(false);
    setDownloading(true);
    downloadJobResult(job.id, job.service)
      .then((blob) => blob.text())
      .then((text) => setDownloadedResult(JSON.parse(text)))
      .catch(() => setDownloadError(true))
      .finally(() => setDownloading(false));
  }

  function handleDownloadJSON() {
    if (!downloadedResult) return;
    const blob = new Blob([JSON.stringify(downloadedResult, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `job-${job.id}-result.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleCloseDialog() {
    onClose();
  }

  function handleOpenDrawer() {
    setView("drawer");
  }

  function handleCloseDrawer() {
    setView("dialog");
  }

  const duration =
    job.startedAt && job.completedAt
      ? Math.round((new Date(job.completedAt).getTime() - new Date(job.startedAt).getTime()) / 1000)
      : null;

  const displayResult = downloadedResult ?? job.parsedResult ?? (() => {
    try { return job.resultPayload ? JSON.parse(job.resultPayload) : undefined; }
    catch { return undefined; }
  })() ?? {};

  const hasResult = displayResult && Object.keys(displayResult as object).length > 0;

  return (
    <>
      <Dialog
        open={view === "dialog"}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderTop: `3px solid ${typeDesign?.primary ?? "#94a3b8"}`,
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            pt: 2.5,
            pb: 1.5,
            background: typeDesign?.gradient ?? "none",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: typeDesign?.text ?? "text.primary" }}>
                  {JOB_TYPE_LABEL[job.jobType] ?? job.jobType}
                </Typography>
                <Chip
                  label={STATUS_LABEL[job.status] ?? job.status}
                  color={STATUS_COLOR[job.status] ?? "default"}
                  size="small"
                  sx={{ fontWeight: 600, height: 22, fontSize: "0.72rem", borderRadius: "6px" }}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Typography component="span" sx={{ ...MONO_SX, color: "text.secondary" }}>
                  Job #{job.id}
                </Typography>
                <Typography component="span" sx={{ ...MONO_SX, color: "text.disabled" }}>·</Typography>
                <Typography component="span" sx={{ ...MONO_SX, color: "text.secondary" }}>
                  Entidad #{job.entityId}
                </Typography>
                {duration != null && (
                  <>
                    <Typography component="span" sx={{ ...MONO_SX, color: "text.disabled" }}>·</Typography>
                    <Typography component="span" sx={{ ...MONO_SX, color: "text.secondary" }}>{duration}s</Typography>
                  </>
                )}
              </Box>

              {job.requestedBy && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                  <Typography variant="caption" color="text.secondary">{job.requestedBy}</Typography>
                </Box>
              )}

              {(job.startedAt || job.completedAt) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                  <ScheduleIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                  <Typography variant="caption" color="text.secondary">
                    {job.startedAt && `Inicio ${formatTime(job.startedAt)}`}
                    {job.startedAt && job.completedAt && " → "}
                    {job.completedAt && `Fin ${formatTime(job.completedAt)}`}
                  </Typography>
                </Box>
              )}
            </Box>
            <IconButton size="small" onClick={handleCloseDialog} sx={{ mt: 0.25, color: "text.disabled" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {(job.status === "Queued" || job.status === "Running") && (
            <Box sx={{ mt: 1.5 }}>
              <LinearProgress
                variant={job.progressPercent != null ? "determinate" : "indeterminate"}
                value={job.progressPercent ?? undefined}
                sx={{ borderRadius: 1, height: 4 }}
              />
              {job.progressMessage && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>{job.progressMessage}</Typography>
              )}
            </Box>
          )}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
          {job.errorMessage && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: "8px", "& .MuiAlert-message": { fontFamily: "monospace", fontSize: "12px" } }}>
              {job.errorMessage}
            </Alert>
          )}

          {isCompleted && (
            <Box>
              {downloadError && !downloading && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}
                  action={<Button size="small" color="error" onClick={handleRetry} sx={{ textTransform: "none" }}>Reintentar</Button>}>
                  Error al cargar el resultado completo
                </Alert>
              )}

              {downloading && (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
                  <Typography variant="caption" color="text.secondary">Cargando resultado...</Typography>
                </Box>
              )}

              {hasResult && !downloading && (
                <JobResultRenderer
                  jobType={job.jobType}
                  parsedResult={displayResult}
                />
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider", gap: 1 }}>
          {isCompleted && !job.errorMessage && hasResult && !downloading && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadOutlined sx={{ fontSize: 14 }} />}
              onClick={handleDownloadJSON}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              Descargar JSON
            </Button>
          )}
          {isCompleted && !job.errorMessage && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              disabled={downloading || downloadError || !hasResult}
              onClick={handleOpenDrawer}
              sx={{ textTransform: "none", mr: "auto", borderRadius: "8px" }}
            >
              Ver detalle completo
            </Button>
          )}
          <Button size="small" onClick={handleCloseDialog} sx={{ textTransform: "none", borderRadius: "8px" }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {isCompleted && hasResult && (
        <JobDetailDrawer
          open={view === "drawer"}
          onClose={handleCloseDrawer}
          job={job}
          parsedResult={displayResult}
        />
      )}
    </>
  );
}
